import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { verifyEmail, resendVerification, formatApiError } from "@/lib/api";
import { ArrowRight, CheckCircle, WarningCircle, SpinnerGap } from "@phosphor-icons/react";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error" | "resend"
  const [err, setErr] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErr("No verification token found in the URL.");
      return;
    }
    verifyEmail(token)
      .then(() => setStatus("success"))
      .catch((ex) => {
        setErr(formatApiError(ex));
        setStatus("error");
      });
  }, [token]);

  const onResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendBusy(true);
    try {
      await resendVerification(resendEmail);
      setResendDone(true);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setResendBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16 flex flex-col items-center gap-4">
        <SpinnerGap size={32} className="text-blue-600 animate-spin" />
        <p className="text-sm text-slate-400">Verifying your email…</p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-center w-12 h-12 bg-green-50 border border-green-200 mb-6 rounded-xl">
          <CheckCircle size={24} className="text-green-600" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          / Verified
        </span>
        <h1 className="font-bold text-4xl mt-3 mb-3 text-slate-900">
          Email verified.
        </h1>
        <p className="text-sm text-slate-500 mb-8">
          Your email address has been confirmed. You now have full access to TrendMing.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs uppercase tracking-wider font-bold transition-colors rounded-lg"
        >
          Go to TrendMing <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    );
  }

  // Error state
  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-16">
      <div className="flex items-center justify-center w-12 h-12 bg-red-50 border border-red-200 mb-6 rounded-xl">
        <WarningCircle size={24} className="text-red-500" />
      </div>
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        / Verification failed
      </span>
      <h1 className="font-bold text-4xl mt-3 mb-3 text-slate-900">
        Link expired.
      </h1>
      <p className="text-sm text-slate-500 mb-2">
        {err || "This verification link is invalid or has expired."}
      </p>
      <p className="text-sm text-slate-400 mb-8">
        Request a new verification link below.
      </p>

      {resendDone ? (
        <div className="border border-green-200 bg-green-50 p-4 text-sm text-green-700 rounded-lg">
          A new verification email has been sent. Check your inbox.
        </div>
      ) : (
        <form onSubmit={onResend} className="space-y-3" data-testid="resend-verification-form">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Your email address
            </label>
            <input
              type="email"
              required
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 px-3 py-3 text-sm rounded-lg text-slate-900"
              data-testid="resend-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={resendBusy}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3 text-sm rounded-lg transition-colors inline-flex items-center justify-center gap-2"
            data-testid="resend-submit-button"
          >
            {resendBusy ? "Sending…" : "Resend verification email"}{" "}
            <ArrowRight size={14} weight="bold" />
          </button>
        </form>
      )}

      <div className="mt-6">
        <Link
          to="/login"
          className="text-xs text-slate-400 hover:text-slate-700 uppercase tracking-wider"
        >
          ← Back to sign in
        </Link>
      </div>
    </div>
  );
}
